import React, { useState, useEffect, useCallback } from "react";
import { partnersService, Partner } from "../../services/partners";

interface PartnerManagementProps {
  onPageChange: (page: string) => void;
}

/** Gift-page rule: default nationwide | shop filter | delivery address */
type GiftRule = "default" | "location_select" | "user_address";

type PartnerFormState = {
  name: string;
  slug: string;
  status: "active" | "inactive";
  giftRule: GiftRule;
};

const emptyForm: PartnerFormState = {
  name: "",
  slug: "",
  status: "active",
  giftRule: "default",
};

function giftRuleFromPartner(partner: Partner): GiftRule {
  const f = partner.fulfillment;
  if (f?.require_user_address) return "user_address";
  if (f?.require_location_select) return "location_select";
  return "default";
}

function fulfillmentFromGiftRule(rule: GiftRule) {
  return {
    require_location_select: rule === "location_select",
    require_user_address: rule === "user_address",
  };
}

function giftRuleLabel(rule: GiftRule) {
  if (rule === "location_select") return "Chọn cửa hàng";
  if (rule === "user_address") return "Giao hàng";
  return "Toàn quốc";
}

const PartnerManagement: React.FC<PartnerManagementProps> = ({
  onPageChange,
}) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [formData, setFormData] = useState<PartnerFormState>(emptyForm);
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    partner: Partner | null;
  }>({ isOpen: false, partner: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchPartners = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await partnersService.getPartners();
      setPartners(response.data || []);
    } catch (err: any) {
      setError(err.message || "Không thể tải danh sách đối tác");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const filteredPartners = partners.filter((partner) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      partner.name.toLowerCase().includes(q) ||
      (partner.slug || "").toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === "all" || partner.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openCreateForm = () => {
    setEditingPartner(null);
    setFormData(emptyForm);
    setFormError("");
    setShowForm(true);
  };

  const openEditForm = (partner: Partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      slug: partner.slug || "",
      status: partner.status,
      giftRule: giftRuleFromPartner(partner),
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPartner(null);
    setFormData(emptyForm);
    setFormError("");
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = formData.name.trim();
    const slug = formData.slug.trim().toLowerCase();
    if (!name) {
      setFormError("Tên đối tác là bắt buộc");
      return;
    }
    if (slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setFormError("Slug chỉ gồm chữ thường, số và dấu gạch ngang (vd: long-chau)");
      return;
    }

    try {
      setIsSaving(true);
      setFormError("");
      const fulfillment = fulfillmentFromGiftRule(formData.giftRule);
      if (editingPartner) {
        await partnersService.updatePartner(editingPartner._id, {
          name,
          status: formData.status,
          ...(slug ? { slug } : {}),
          fulfillment,
        });
        showToast("Cập nhật đối tác thành công");
      } else {
        await partnersService.createPartner({
          name,
          status: formData.status,
          ...(slug ? { slug } : {}),
          fulfillment,
        });
        showToast("Tạo đối tác thành công");
      }
      closeForm();
      await fetchPartners();
    } catch (err: any) {
      setFormError(err.message || "Không thể lưu đối tác");
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal.partner) return;
    try {
      setIsDeleting(true);
      await partnersService.deletePartner(deleteModal.partner._id);
      setDeleteModal({ isOpen: false, partner: null });
      showToast("Xóa đối tác thành công");
      await fetchPartners();
    } catch (err: any) {
      showToast(err.message || "Không thể xóa đối tác", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const statusLabel = (status: Partner["status"]) =>
    status === "active" ? "Hoạt động" : "Ngưng hoạt động";

  const statusBadgeClass = (status: Partner["status"]) =>
    status === "active"
      ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
      : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300";

  const copyText = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard?.writeText(text);
      showToast(successMessage);
    } catch {
      showToast("Không thể sao chép", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">
          Đang tải danh sách đối tác...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={fetchPartners}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Quản lý Đối tác
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Quản lý chuỗi nhà thuốc và kho voucher
          </p>
        </div>
        <button
          onClick={openCreateForm}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          + Thêm đối tác
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px] max-w-md relative">
          <input
            type="text"
            placeholder="Tìm theo tên đối tác..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tất cả trạng thái</option>
          <option value="active">Hoạt động</option>
          <option value="inactive">Ngưng hoạt động</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Tên đối tác
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Link chiến dịch
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Quy tắc đổi quà
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Ngày tạo
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPartners.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    {partners.length === 0
                      ? "Chưa có đối tác nào. Hãy thêm đối tác đầu tiên."
                      : "Không tìm thấy đối tác phù hợp."}
                  </td>
                </tr>
              ) : (
                filteredPartners.map((partner) => (
                  <tr
                    key={partner._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <button
                        onClick={() => onPageChange(`partners/${partner._id}`)}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline text-left"
                      >
                        {partner.name}
                      </button>
                    </td>
                    <td className="px-4 py-4 text-sm max-w-[160px]">
                      {partner.slug ? (
                        <span
                          className="font-mono text-xs text-gray-700 dark:text-gray-300"
                          title={partner.slug}
                        >
                          {partner.slug}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm max-w-xs">
                      {partner.campaign_link ? (
                        <div className="flex items-start gap-2">
                          <a
                            href={partner.campaign_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                            title={
                              partner.campaign_name || partner.campaign_link
                            }
                          >
                            {partner.campaign_link}
                          </a>
                          <button
                            type="button"
                            onClick={() =>
                              copyText(
                                partner.campaign_link || "",
                                "Đã sao chép link chiến dịch"
                              )
                            }
                            className="shrink-0 px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Sao chép
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">
                          Không có chiến dịch
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {(() => {
                        const rule = giftRuleFromPartner(partner);
                        const badge =
                          rule === "location_select"
                            ? "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300"
                            : rule === "user_address"
                              ? "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300";
                        return (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge}`}
                          >
                            {giftRuleLabel(rule)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(
                          partner.status
                        )}`}
                      >
                        {statusLabel(partner.status)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                      {new Date(partner.createdAt).toLocaleDateString("vi-VN")}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() =>
                            onPageChange(`partners/${partner._id}`)
                          }
                          className="px-2 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                          title="Chi tiết"
                        >
                          Chi tiết
                        </button>
                        <button
                          onClick={() => openEditForm(partner)}
                          className="px-2 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          title="Chỉnh sửa"
                        >
                          Sửa
                        </button>
                        <button
                          onClick={() =>
                            setDeleteModal({ isOpen: true, partner })
                          }
                          className="px-2 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                          title="Xóa"
                        >
                          Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingPartner ? "Chỉnh sửa đối tác" : "Thêm đối tác"}
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label
                  htmlFor="partner-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Tên đối tác
                </label>
                <input
                  id="partner-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nhập tên đối tác"
                  autoFocus
                />
              </div>
              <div>
                <label
                  htmlFor="partner-slug"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Slug (link ngắn)
                </label>
                <input
                  id="partner-slug"
                  type="text"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      slug: e.target.value.toLowerCase().replace(/\s+/g, "-"),
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="vd: long-chau (để trống = tự tạo từ tên)"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Link dạng gift.pregnavie.vn/long-chau (theo landing của chiến
                  dịch)
                </p>
              </div>
              <div>
                <label
                  htmlFor="partner-gift-rule"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Quy tắc đổi quà (trang gift)
                </label>
                <select
                  id="partner-gift-rule"
                  value={formData.giftRule}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      giftRule: e.target.value as GiftRule,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="default">
                    Mặc định — đổi quà tất cả chi nhánh toàn quốc
                  </option>
                  <option value="location_select">
                    Ngoại lệ — chọn cửa hàng theo tỉnh/quận (vd: Long Châu)
                  </option>
                  <option value="user_address">
                    Ngoại lệ — giao hàng, yêu cầu địa chỉ người nhận (vd: ECO)
                  </option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Chỉ chọn một ngoại lệ. “Chọn cửa hàng” cần import danh sách
                  địa điểm; “Giao hàng” không dùng danh sách cửa hàng.
                </p>
              </div>
              <div>
                <label
                  htmlFor="partner-status"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Trạng thái
                </label>
                <select
                  id="partner-status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value as "active" | "inactive",
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Hoạt động</option>
                  <option value="inactive">Ngưng hoạt động</option>
                </select>
              </div>
              {formError && (
                <div className="text-sm text-red-600 dark:text-red-400">
                  {formError}
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSaving ? "Đang lưu..." : "Lưu"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal.isOpen && deleteModal.partner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Xác nhận xóa
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Bạn có chắc muốn xóa đối tác{" "}
              <span className="font-medium">{deleteModal.partner.name}</span>?
              Hành động này không thể hoàn tác.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, partner: null })}
                disabled={isDeleting}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? "Đang xóa..." : "Xóa"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-md shadow-lg text-white ${
              toast.type === "success" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerManagement;
